import { useCallback, useEffect, useState } from 'react'
import type { Stat, TitreEnMainDTO } from '@whatremains/shared'
import { api } from '../api'

/* Les mêmes noms que la poupée. Une table de cinq mots, dupliquée plutôt
   qu'importée d'un écran voisin : elle appartient au vocabulaire, pas à la
   poupée, et la faire voyager entre deux composants les lierait pour rien. */
const NOMS_STATS: Record<Stat, string> = {
  force: 'Force',
  adresse: 'Adresse',
  esprit: 'Esprit',
  vigueur: 'Vigueur',
  fortune: 'Fortune',
}

/**
 * Les noms qu'on s'est faits, et ceux qui sont encore devant.
 *
 * ## Ce qui existait, et ce qui manquait
 *
 * Enzo : « on n'a pas mis en place de système de titre encore je crois ? Ce
 * serait bien de le faire et de tout brancher. D'avoir des titres de plus en
 * plus badass et qu'ils soient attribués en fonction des choix, donjons,
 * quêtes, niveau atteint, niveau de maîtrise sur certaines stats. »
 *
 * Il était là presque en entier : seize titres écrits en base avec leurs bonus,
 * une table de possession, trois routes serveur, le bonus déjà compté dans les
 * statistiques, et un `titreActif` affiché sur la fiche. Deux choses
 * manquaient, et chacune suffisait à tout rendre mort : **rien n'insérait
 * jamais** dans la table de possession, et **le client n'appelait aucune des
 * trois routes**.
 *
 * ## Pourquoi les non-obtenus figurent
 *
 * « De plus en plus badass » décrit une échelle, et une échelle dont on ne voit
 * pas les barreaux ne donne envie de rien. Les paliers encore devant soi sont
 * donc listés avec ce qu'ils demandent.
 *
 * Les titres de RÉCIT n'apparaissent qu'une fois gagnés, et c'est le serveur
 * qui le garantit : les annoncer dirait ce que les quêtes réservent, et un
 * titre qui vend la mèche de la quête qui le donne vaut moins que pas de titre.
 *
 * ## Détruire
 *
 * Le bouton ne dit pas ce qu'il déclenche, et c'est voulu : c'est une épreuve
 * du Retrait de Novacula. Il dit seulement que rien ne le rend, ce qui est vrai
 * et suffisant pour décider. Le joueur qui le fait doit le faire pour ses
 * propres raisons, sans quoi le geste ne compte pas.
 */

const RANGS = ['', 'premier', 'deuxième', 'troisième', 'quatrième', 'dernier']

function bonusEnClair(bonus: TitreEnMainDTO['titre']['bonus']): string | null {
  const paires = Object.entries(bonus)
  if (paires.length === 0) return null
  return paires
    .map(([s, v]) => `${NOMS_STATS[s as Stat]} +${v}`)
    .join(', ')
}

export function Titres({
  id,
  surChangement,
}: {
  id: string
  /** Un titre porté change les statistiques : la fiche doit se relire. */
  surChangement: () => void
}) {
  const [liste, setListe] = useState<TitreEnMainDTO[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  /* La destruction est irréversible : elle se confirme sur la ligne même,
     jamais dans une fenêtre qu'on valide par réflexe. */
  const [aDetruire, setADetruire] = useState<string | null>(null)

  const recharger = useCallback(async () => setListe(await api.titres(id)), [id])
  useEffect(() => { void recharger() }, [recharger])

  const agir = async (f: () => Promise<unknown>) => {
    setErreur(null)
    try {
      await f()
      await recharger()
      surChangement()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Ça n’a pas marché.')
    }
  }

  const tenus = liste.filter((t) => t.obtenu && !t.detruit)
  const perdus = liste.filter((t) => t.detruit)
  const devant = liste.filter((t) => !t.obtenu)
  const porte = tenus.find((t) => t.actif)

  return (
    <section>
      <div className="surtitre">Ce qu’on t’appelle</div>

      {erreur && <p className="erreur">{erreur}</p>}

      {tenus.length === 0 ? (
        <p className="tenu">
          Aucun nom pour l’instant. On te les donne, tu ne les prends pas.
        </p>
      ) : (
        <div className="titres">
          {tenus.map((t) => (
            <div key={t.titre.code} className={`titre-ligne${t.actif ? ' porte' : ''}`}>
              <div className="titre-corps">
                <b>{t.titre.nom}</b>
                <span className="titre-rang">{RANGS[t.titre.rang]} rang</span>
                {t.titre.texte && <i>{t.titre.texte}</i>}
                {bonusEnClair(t.titre.bonus) && (
                  <span className="titre-bonus">{bonusEnClair(t.titre.bonus)}</span>
                )}
              </div>
              <div className="titre-actions">
                <button
                  className="discret"
                  onClick={() => void agir(() => api.arborer(id, t.actif ? null : t.titre.code))}
                >
                  {t.actif ? 'ne plus porter' : 'porter'}
                </button>
                {aDetruire === t.titre.code ? (
                  <>
                    <button
                      className="discret erreur-tenue"
                      onClick={() => {
                        setADetruire(null)
                        void agir(() => api.detruireTitre(id, t.titre.code))
                      }}
                    >
                      rien ne le rend · confirmer
                    </button>
                    <button className="discret" onClick={() => setADetruire(null)}>
                      garder
                    </button>
                  </>
                ) : (
                  <button className="discret" onClick={() => setADetruire(t.titre.code)}>
                    retirer ce nom
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {porte && (
        <p className="tenu" style={{ marginTop: 12 }}>
          Un seul à la fois : porter celui-ci en repose un autre.
        </p>
      )}

      {devant.length > 0 && (
        <>
          <div className="surtitre" style={{ marginTop: 30 }}>Ce qu’il reste à mériter</div>
          <div className="titres">
            {devant.map((t) => (
              <div key={t.titre.code} className="titre-ligne devant">
                <div className="titre-corps">
                  <b>{t.titre.nom}</b>
                  <span className="titre-rang">{RANGS[t.titre.rang]} rang</span>
                  {t.exigence && <i>{t.exigence}</i>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {perdus.length > 0 && (
        <>
          <div className="surtitre" style={{ marginTop: 30 }}>Ce que tu as retiré</div>
          {/* On ne les cache pas : le joueur doit pouvoir se souvenir de ce
              qu'il a retiré, sinon le geste n'a plus de poids une fois fait. */}
          <div className="titres">
            {perdus.map((t) => (
              <div key={t.titre.code} className="titre-ligne perdu">
                <div className="titre-corps">
                  <b>{t.titre.nom}</b>
                  <i>Retiré. Rien ne le rend.</i>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
